import { FilterExpression } from '@pushwoosh/rpc-service-client/pushwoosh/objects/filters/v1/model_pb';
import {
  isConditionsGroup,
  isBetweenOperator,
  isDaysAgoOperator,
  isMultiOperator,
  isValueLessOperator,
} from './helpers';
import {
  type Condition,
  type ConditionTag,
  type ConditionEvent,
  type ConditionValue,
  type GroupOperator,
} from '../../types';
import { type OperatorConfig, valuesTypes, operators } from './common';
import EventCountOperator = FilterExpression.Event.Count.Operator;
import OperationOperator = FilterExpression.Operation.Operator;

function getDaysAgoOperator(op: string, ObjClass: any): any {
  const { Operator } = ObjClass.Date.DaysAgo;
  const daysAgoOperatorsMap: Record<string, any> = {
    eq: Operator.OPERATOR_EQ,
    noteq: Operator.OPERATOR_NEQ,
    lte: Operator.OPERATOR_LTE,
    gte: Operator.OPERATOR_GTE,
    between: Operator.OPERATOR_BETWEEN,
  };
  return daysAgoOperatorsMap[op];
}

function convertConditionValue<C extends { new (): any }>(
  { name, type, values, operator }: ConditionValue,
  ObjClass: C,
): InstanceType<C> {
  const feTag = new ObjClass();
  feTag.setName(name);
  const valTypeConf = valuesTypes.find((vtc) => type === vtc.tagType)!;
  // new FilterExpression.Tag.Integer()
  const tagTypeObj = new (ObjClass as any)[valTypeConf.method]();
  const operatorConf = operators.find(
    (op) => op.checkOperator?.(operator) ?? op.operator === operator,
  ) as OperatorConfig;
  // new FilterExpression.Tag.Integer.Eq()
  const operatorObj = new (ObjClass as any)[valTypeConf.method][
    operatorConf.method
  ]();
  const finalValues = valTypeConf.convertToDb
    ? values.map(valTypeConf.convertToDb)
    : values;
  if (operator === 'between') {
    operatorObj.setFrom(finalValues[0]);
    operatorObj.setTo(finalValues[1]);
  } else if (isMultiOperator(operator)) {
    operatorObj.setValuesList(finalValues);
  } else if (isDaysAgoOperator(operator)) {
    const [, daOperator] = operator.split(' ');
    operatorObj.setOperator(getDaysAgoOperator(daOperator, ObjClass));
    if (isBetweenOperator(operator)) {
      operatorObj.setValuesList(finalValues);
    } else {
      operatorObj.setValue(finalValues[0]);
    }
  } else if (isValueLessOperator(operator)) {
    operatorObj.setExists(operator === 'any');
  } else {
    operatorObj.setValue(finalValues[0]);
  }
  tagTypeObj[`set${operatorConf.method}`](operatorObj);
  feTag[`set${valTypeConf.method}`](tagTypeObj);
  return feTag;
}

function convertTag(
  applicationCode: string,
  { tag }: ConditionTag<ConditionValue>,
): FilterExpression {
  const expression = new FilterExpression();
  const feTag = convertConditionValue(tag, FilterExpression.Tag);
  feTag.setApplication(applicationCode);
  expression.setTag(feTag);
  return expression;
}

const eventCountOperatorsMap: Record<string, EventCountOperator> = {
  eq: EventCountOperator.OPERATOR_EQ,
  lte: EventCountOperator.OPERATOR_LTE,
  gte: EventCountOperator.OPERATOR_GTE,
};

function getEventCountOperator(op: string): EventCountOperator {
  return eventCountOperatorsMap[op];
}

function convertEvent(
  applicationCode: string,
  {
    event: { name, count, lastDays, attributes },
  }: ConditionEvent<ConditionValue>,
) {
  const expression = new FilterExpression();
  const feEvent = new FilterExpression.Event();
  feEvent.setEvent(name);
  feEvent.setApplication(applicationCode);

  const eventCount = new FilterExpression.Event.Count();
  eventCount.setOperator(getEventCountOperator(count.operator));
  eventCount.setValue(count.values[0]);
  feEvent.setCount(eventCount);

  const date = new FilterExpression.Event.Date();
  const daysAgo = new FilterExpression.Event.Date.DaysAgo();
  daysAgo.setOperator(
    FilterExpression.Event.Date.DaysAgo.Operator.OPERATOR_LTE,
  );
  daysAgo.setValue(lastDays);
  date.setDaysAgo(daysAgo);
  feEvent.setDate(date);

  if (attributes.length > 0) {
    feEvent.setAttributesList(
      attributes.map((attr) => {
        return convertConditionValue(attr, FilterExpression.Event.Attribute);
      }),
    );
  }

  expression.setEvent(feEvent);
  return expression;
}

function toOperationOperator(op: GroupOperator): OperationOperator {
  const daysAgoOperatorsMap: Record<GroupOperator, OperationOperator> = {
    and: OperationOperator.OPERATOR_INTERSECT,
    or: OperationOperator.OPERATOR_UNION,
    'and not': OperationOperator.OPERATOR_DIFFERENCE,
  };
  return daysAgoOperatorsMap[op];
}
export function toFilterExpression(
  applicationCode: string,
  condition: Condition,
): FilterExpression {
  if (isConditionsGroup(condition)) {
    const expression = new FilterExpression();
    const operation = new FilterExpression.Operation();
    operation.setOperator(toOperationOperator(condition.operator));
    const operandsList = condition.conditions.map((cond) => {
      return toFilterExpression(applicationCode, cond);
    });
    operation.setOperandsList(operandsList);
    expression.setOperation(operation);
    return expression;
  }
  if (condition.type === 'tag') {
    return convertTag(applicationCode, condition);
  }
  if (condition.type === 'event') {
    return convertEvent(applicationCode, condition);
  }
  // eslint-disable-next-line no-console
  console.warn('Unknown condition type', condition);
  return new FilterExpression();
}
